import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, LogOut, Search, Filter, Trash2, MapPin, Loader2, Navigation, X, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api from "@/lib/api";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { PROPERTY_TYPES, getPropertyType } from "@/lib/propertyTypes";
import { formatDate } from "@/lib/detailSchemas";
import ExpertiseMap from "@/components/ExpertiseMap";
import ExpertiseForm from "@/components/ExpertiseForm";
import ExpertiseDetailPanel, { summaryLine } from "@/components/ExpertiseDetailSheet";
import NearbySearchDialog from "@/components/NearbySearchDialog";
import GuidedTour from "@/components/GuidedTour";

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [pickMode, setPickMode] = useState(false);
  const [pickedLatLng, setPickedLatLng] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [focus, setFocus] = useState(null);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [openDetailId, setOpenDetailId] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [nearbyOpen, setNearbyOpen] = useState(false);
  const [mobileView, setMobileView] = useState("list"); // 'list' | 'map' — only affects mobile screens

  // Auto-switch to map view on mobile when user needs to interact with the map (pick location).
  useEffect(() => {
    if (pickMode && typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches) {
      setMobileView("map");
    }
  }, [pickMode]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/expertises");
      setItems(data.items);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim();
    // Detect "ada/parsel" or "ada-parsel" pattern (e.g. "726/5")
    const adaParselMatch = q.match(/^(\d+)\s*[\/\-]\s*(\d+)$/);
    const trLower = (s) => (s || "").toLocaleLowerCase("tr");
    const qLow = trLower(q);

    return items.filter((it) => {
      if (filterType !== "all" && it.property_type !== filterType) return false;
      if (!q) return true;

      if (adaParselMatch) {
        // Exact ada AND parsel match
        return (
          String(it.ada) === adaParselMatch[1] && String(it.parsel) === adaParselMatch[2]
        );
      }

      const blob = trLower(
        `${it.province_name} ${it.district_name} ${it.neighborhood_name} ${it.ada} ${it.parsel} ${it.notes || ""}`,
      );
      return blob.includes(qLow);
    });
  }, [items, search, filterType]);

  const startAdd = () => {
    setEditingItem(null);
    setShowForm(true);
    setPickMode(true);
    setPickedLatLng(null);
  };

  // Right-click on map → open form at that location with pin already placed.
  const startAddAtLocation = (latlng) => {
    setEditingItem(null);
    setPickedLatLng({ lat: latlng.lat, lng: latlng.lng });
    setPickMode(true);
    setShowForm(true);
    setFocus({ center: [latlng.lat, latlng.lng], zoom: 17 });
    toast.success("Konum işaretlendi — bilgileri girip kaydedin");
  };

  const startEdit = (item) => {
    setOpenDetailId(null);
    setEditingItem(item);
    setPickedLatLng({ lat: item.latitude, lng: item.longitude });
    setPickMode(false);
    setShowForm(true);
    setFocus({ center: [item.latitude, item.longitude], zoom: 16 });
  };

  const startDuplicate = (item) => {
    // Clone the item WITHOUT id/user_id/created_at so the form treats it as
    // a fresh record. Ada/parsel are cleared so the user must enter new ones.
    setOpenDetailId(null);
    const { id, user_id, created_at, updated_at, ada, parsel, ...rest } = item;
    setEditingItem({ ...rest, ada: "", parsel: "" });
    setPickedLatLng({ lat: item.latitude, lng: item.longitude });
    setPickMode(true); // allow user to move pin if needed
    setShowForm(true);
    setFocus({ center: [item.latitude, item.longitude], zoom: 16 });
    toast.info("Kayıt çoğaltıldı — yeni ada/parsel numarası girin", {
      duration: 4000,
    });
  };

  const cancelAdd = () => {
    setShowForm(false);
    setPickMode(false);
    setPickedLatLng(null);
    setEditingItem(null);
  };

  const handleCreated = (item) => {
    setItems((prev) => [item, ...prev]);
    setShowForm(false);
    setPickMode(false);
    setPickedLatLng(null);
    setEditingItem(null);
    setActiveId(item.id);
    setFocus({ center: [item.latitude, item.longitude], zoom: 15 });
  };

  const handleUpdated = (item) => {
    setItems((prev) => prev.map((x) => (x.id === item.id ? item : x)));
    setShowForm(false);
    setPickMode(false);
    setPickedLatLng(null);
    setEditingItem(null);
    setActiveId(item.id);
    setFocus({ center: [item.latitude, item.longitude], zoom: 16 });
    setOpenDetailId(item.id);
  };

  const selectItem = (it) => {
    setActiveId(it.id);
    setFocus({ center: [it.latitude, it.longitude], zoom: 15 });
    setOpenDetailId(it.id);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/expertises/${deleteTarget}`);
      setItems((prev) => prev.filter((x) => x.id !== deleteTarget));
      toast.success("Kayıt silindi");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setDeleteTarget(null);
    }
  };

  return (
    <div className="h-screen w-screen flex flex-col md:flex-row bg-white text-zinc-900 overflow-hidden">
      {/* Sidebar — full width on mobile (list/detail/form), responsive on md+ */}
      <aside
        className={`${mobileView === "list" ? "flex flex-1" : "hidden"} md:flex md:flex-none ${
          showForm || openDetailId ? "md:w-[50vw] md:min-w-[520px] md:max-w-[960px]" : "md:w-[420px]"
        } w-full shrink-0 border-r border-zinc-200 flex-col bg-white z-10 md:transition-[width] md:duration-200 min-h-0`}
      >
        <header className="border-b border-zinc-200 px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 grid place-items-center bg-zinc-900 text-white rounded-sm">
              <MapPin className="h-4.5 w-4.5" strokeWidth={2.5} />
            </div>
            <div>
              <div className="text-sm font-semibold tracking-tight leading-none">Kolay Arşivim</div>
              <div className="text-[11px] text-zinc-500 mt-1" data-testid="user-email">
                {user && user.email}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-0.5">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileView("map")}
            aria-label="Haritayı göster"
            data-testid="mobile-show-map-btn"
            className="md:hidden"
            title="Haritayı göster"
          >
            <MapPin className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            asChild
            aria-label="Ayarlar"
            data-testid="open-settings-btn"
          >
            <Link to="/ayarlar">
              <Settings className="h-4 w-4" />
            </Link>
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={logout}
            aria-label="Çıkış"
            data-testid="logout-btn"
          >
            <LogOut className="h-4 w-4" />
          </Button>
          </div>
        </header>

        {!showForm && !openDetailId ? (
          <>
            <div className="border-b border-zinc-200 px-5 py-4 space-y-3">
              <Button
                onClick={startAdd}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm h-10"
                data-testid="add-expertise-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Yeni Ekspertiz Ekle
              </Button>

              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      // Search is already reactive; blur to signal completion
                      e.currentTarget.blur();
                    }
                  }}
                  placeholder="Mahalle, ilçe veya 726/5"
                  className="pl-9 pr-9 rounded-sm"
                  data-testid="search-input"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 grid place-items-center text-zinc-400 hover:text-zinc-900 rounded-sm"
                    aria-label="Aramayı temizle"
                    data-testid="clear-search-btn"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              {search && (
                <div
                  className="text-[11px] text-zinc-500 font-mono px-0.5"
                  data-testid="search-result-count"
                >
                  <span className="text-zinc-900 font-semibold">{filtered.length}</span> sonuç
                  {" · "}
                  <span className="text-zinc-400">"{search}"</span>
                </div>
              )}

              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger data-testid="filter-type-select" className="rounded-sm">
                  <div className="flex items-center gap-2">
                    <Filter className="h-3.5 w-3.5 text-zinc-500" />
                    <SelectValue />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm türler</SelectItem>
                  {PROPERTY_TYPES.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      <div className="flex items-center gap-2">
                        <span
                          className="inline-block h-2.5 w-2.5 rounded-full"
                          style={{ background: p.color }}
                        />
                        {p.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex-1 overflow-y-auto" data-testid="expertise-list">
              {loading ? (
                <div className="p-10 flex items-center justify-center text-zinc-500">
                  <Loader2 className="h-5 w-5 animate-spin" />
                </div>
              ) : filtered.length === 0 ? (
                <div className="px-5 py-10 text-center">
                  <div className="mx-auto h-12 w-12 grid place-items-center rounded-sm bg-zinc-100 text-zinc-400">
                    <MapPin className="h-5 w-5" />
                  </div>
                  <p className="mt-3 text-sm text-zinc-600">
                    Henüz ekspertiz kaydınız yok
                  </p>
                  <p className="mt-1 text-xs text-zinc-400">
                    Yeni bir kayıt ekleyerek başlayın
                  </p>
                </div>
              ) : (
                <ul className="divide-y-2 divide-zinc-200">
                  {filtered.map((it) => {
                    const type = getPropertyType(it.property_type);
                    const active = it.id === activeId;
                    return (
                      <li
                        key={it.id}
                        className={`group px-5 py-4 cursor-pointer transition-colors duration-150 ${
                          active ? "bg-zinc-50" : "hover:bg-zinc-50"
                        }`}
                        onClick={() => selectItem(it)}
                        data-testid={`expertise-item-${it.id}`}
                      >
                        <div className="flex items-start gap-3 min-w-0">
                          <span
                            className="mt-1 inline-block h-2.5 w-2.5 rounded-full shrink-0"
                            style={{ background: type.color }}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge
                                variant="outline"
                                className="rounded-sm px-1.5 py-0 text-[10px] font-medium border-zinc-300"
                                style={{ color: type.color, borderColor: `${type.color}55` }}
                              >
                                {type.label}
                              </Badge>
                              <span
                                className="text-[11px] font-mono font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-sm px-1.5 py-0.5"
                                data-testid={`expertise-adaparsel-${it.id}`}
                              >
                                Ada {it.ada} · Parsel {it.parsel}
                              </span>
                              {it.expertise_date && (
                                <span
                                  className="ml-auto text-[10px] font-mono font-semibold bg-amber-100 text-amber-900 border border-amber-300 rounded-sm px-1.5 py-0.5"
                                  data-testid={`expertise-date-${it.id}`}
                                >
                                  {formatDate(it.expertise_date)}
                                </span>
                              )}
                            </div>
                            <div className="text-sm font-medium text-zinc-900 truncate">
                              {it.neighborhood_name} Mah.
                            </div>
                            <div className="text-xs text-zinc-500 truncate">
                              {it.district_name} / {it.province_name}
                            </div>
                            {summaryLine(it) && (
                              <div className="mt-1 text-[11px] text-zinc-600 font-mono truncate">
                                {summaryLine(it)}
                              </div>
                            )}
                            {it.notes ? (
                              <div className="mt-1.5 text-xs text-zinc-600 line-clamp-2">
                                {it.notes}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <footer className="border-t border-zinc-200 px-5 py-3 flex items-center justify-between text-xs text-zinc-500">
              <span>Toplam: <strong className="text-zinc-900">{filtered.length}</strong></span>
              <span>{items.length} kayıt</span>
            </footer>
          </>
        ) : showForm ? (
          <ExpertiseForm
            key={editingItem?.id || "new"}
            initial={editingItem}
            items={items}
            pickedLatLng={pickedLatLng}
            onEnablePick={() => {
              setPickMode(true);
              toast.info("Harita üzerinde konum seçin");
            }}
            onLookupLocation={(latlng) => {
              setPickedLatLng(latlng);
              setPickMode(true);
              setFocus({ center: [latlng.lat, latlng.lng], zoom: 16 });
            }}
            onCreated={handleCreated}
            onUpdated={handleUpdated}
            onCancel={cancelAdd}
          />
        ) : (
          <ExpertiseForm
            key={`view-${openDetailId}`}
            readOnly
            initial={items.find((x) => x.id === openDetailId) || null}
            items={items}
            onCancel={() => setOpenDetailId(null)}
            onEdit={(item) => startEdit(item)}
            onDuplicate={(item) => startDuplicate(item)}
            onDelete={(id) => {
              setOpenDetailId(null);
              setDeleteTarget(id);
            }}
          />
        )}
      </aside>

      {/* Map */}
      <main className={`${mobileView === "map" ? "flex flex-1" : "hidden"} md:flex md:flex-1 relative min-h-0`}>
        {/* Mobile-only floating button to go back to list */}
        <button
          type="button"
          onClick={() => setMobileView("list")}
          className="md:hidden absolute top-3 left-3 z-[1000] h-9 px-3 rounded-sm bg-white shadow-md border border-zinc-200 text-xs font-semibold text-zinc-900 flex items-center gap-1.5"
          data-testid="mobile-back-to-list"
        >
          ← Liste
        </button>
        {pickMode && (
          <div
            className="absolute top-4 left-1/2 -translate-x-1/2 z-[1000] bg-zinc-900 text-white text-xs px-3 py-2 rounded-sm shadow-lg"
            data-testid="pick-hint"
          >
            Konumu belirlemek için haritaya tıklayın
          </div>
        )}
        <ExpertiseMap
          items={items}
          activeId={activeId}
          focus={focus}
          pickMode={pickMode}
          pickedLatLng={pickedLatLng}
          onPick={(latlng) => setPickedLatLng(latlng)}
          onSelect={(it) => {
            setActiveId(it.id);
            setOpenDetailId(it.id);
          }}
          onAddAt={startAddAtLocation}
          fitItems={filtered}
          fitSignal={`${search.trim().toLowerCase()}|${filterType}|${items.length}`}
        />
        {/* Legend */}
        <div className="absolute bottom-4 right-4 z-[1000] bg-white border border-zinc-200 rounded-sm shadow-sm p-3 text-xs">
          <div className="font-semibold text-zinc-900 mb-2 tracking-tight">Taşınmaz Türü</div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
            {PROPERTY_TYPES.map((p) => (
              <div key={p.value} className="flex items-center gap-2">
                <span
                  className="inline-block h-2.5 w-2.5 rounded-full"
                  style={{ background: p.color }}
                />
                <span className="text-zinc-700">{p.label}</span>
              </div>
            ))}
          </div>
        </div>
      </main>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Kaydı sil</AlertDialogTitle>
            <AlertDialogDescription>
              Bu ekspertiz kaydını silmek istediğinize emin misiniz? Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <NearbySearchDialog
        open={nearbyOpen}
        onOpenChange={setNearbyOpen}
        items={items}
        onSelect={(item) => {
          setActiveId(item.id);
          setFocus({ center: [item.latitude, item.longitude], zoom: 17 });
          setOpenDetailId(item.id);
        }}
      />
      <GuidedTour />
    </div>
  );
}
